import React from 'react';
import styled from 'styled-components';

// Convert #AARRGGBB → CSS rgba()
function argbToCss(hex: string | null | undefined, fallback = '#888'): string {
  if (!hex) return fallback;
  const c = hex.replace('#', '');
  if (c.length !== 8) return fallback;
  const a = (parseInt(c.slice(0, 2), 16) / 255).toFixed(2);
  const r = parseInt(c.slice(2, 4), 16);
  const g = parseInt(c.slice(4, 6), 16);
  const b = parseInt(c.slice(6, 8), 16);
  return `rgba(${r},${g},${b},${a})`;
}

const Tile = styled.div<{ isDragOver?: boolean; isDragging?: boolean }>`
  position: relative;
  border-radius: 16px;
  overflow: hidden;
  border: 2px solid ${({ isDragOver }) => (isDragOver ? '#4a90e2' : '#ddd')};
  aspect-ratio: 0.9;
  cursor: pointer;
  transition: border-color 0.15s, transform 0.15s, opacity 0.15s;
  opacity: ${({ isDragging }) => (isDragging ? 0.4 : 1)};
  &:hover {
    border-color: ${({ isDragOver }) => (isDragOver ? '#4a90e2' : '#555')};
    transform: scale(1.01);
  }
`;

const Background = styled.div<{ bg: string; bgImage?: string | null }>`
  position: absolute;
  inset: 0;
  background-color: ${({ bg }) => bg};
  ${({ bgImage }) =>
    bgImage
      ? `background-image: url(${bgImage}); background-size: cover; background-position: center;`
      : ''}
`;

const PreviewCover = styled.img`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const BoardArea = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding-bottom: 20%;
`;

const BoardRect = styled.div<{ bg: string; bgImage?: string | null }>`
  width: 62%;
  aspect-ratio: 1;
  border-radius: 8px;
  background-color: ${({ bg }) => bg};
  overflow: hidden;
  ${({ bgImage }) =>
    bgImage
      ? `background-image: url(${bgImage}); background-size: cover; background-position: center;`
      : ''}
`;

const LabelBar = styled.div<{ bg: string }>`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: ${({ bg }) => bg};
  padding: 6px 8px;
  text-align: center;
`;

const LabelText = styled.span<{ color: string }>`
  font-size: 13px;
  font-weight: 600;
  color: ${({ color }) => color};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  display: block;
`;

const ActionBar = styled.div`
  position: absolute;
  top: 8px;
  right: 8px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  z-index: 2;
`;

const ActionBtn = styled.button<{ variant?: 'edit' | 'delete' | 'move' }>`
  border: none;
  border-radius: 6px;
  padding: 4px 10px;
  font-size: 11px;
  font-weight: 700;
  cursor: pointer;
  background: ${({ variant }) => {
    if (variant === 'delete') return 'rgba(220,50,50,0.85)';
    if (variant === 'move') return 'rgba(60,60,60,0.80)';
    return 'rgba(40,40,40,0.75)';
  }};
  color: #fff;
  letter-spacing: 0.3px;
  backdrop-filter: blur(2px);
  &:hover {
    opacity: 0.9;
  }
  &:disabled {
    opacity: 0.35;
    cursor: not-allowed;
  }
`;

const OrderBadge = styled.div`
  position: absolute;
  top: 8px;
  left: 8px;
  background: rgba(0, 0, 0, 0.65);
  color: #fff;
  font-size: 12px;
  font-weight: 700;
  border-radius: 8px;
  padding: 3px 8px;
  backdrop-filter: blur(2px);
  z-index: 2;
`;

const DragHandle = styled.div`
  position: absolute;
  top: 8px;
  left: 50%;
  transform: translateX(-50%);
  color: rgba(255, 255, 255, 0.8);
  font-size: 18px;
  cursor: grab;
  z-index: 2;
  user-select: none;
  &:active {
    cursor: grabbing;
  }
`;

export interface ThemeGridItemPreviewProps {
  name: string;
  previewUrl?: string | null;
  gameplayBackgroundColor?: string | null;
  gameplayBackgroundImageUrl?: string | null;
  boardBackgroundColor?: string | null;
  boardBackgroundImageUrl?: string | null;
  buttonsBackgroundColor: string;
  buttonsIconsColor: string;
  onEdit: () => void;
  onDelete: () => void;
  // Reorder mode props
  isReorderMode?: boolean;
  positionIndex?: number;
  canMoveUp?: boolean;
  canMoveDown?: boolean;
  onMoveUp?: () => void;
  onMoveDown?: () => void;
  isDragging?: boolean;
  isDragOver?: boolean;
  onDragStart?: (e: React.DragEvent) => void;
  onDragOver?: (e: React.DragEvent) => void;
  onDragLeave?: (e: React.DragEvent) => void;
  onDrop?: (e: React.DragEvent) => void;
  onDragEnd?: (e: React.DragEvent) => void;
}

const ThemeGridItemPreview: React.FC<ThemeGridItemPreviewProps> = ({
  name,
  previewUrl,
  gameplayBackgroundColor,
  gameplayBackgroundImageUrl,
  boardBackgroundColor,
  boardBackgroundImageUrl,
  buttonsBackgroundColor,
  buttonsIconsColor,
  onEdit,
  onDelete,
  isReorderMode = false,
  positionIndex,
  canMoveUp,
  canMoveDown,
  onMoveUp,
  onMoveDown,
  isDragging,
  isDragOver,
  onDragStart,
  onDragOver,
  onDragLeave,
  onDrop,
  onDragEnd,
}) => {
  const bgCss = argbToCss(gameplayBackgroundColor, '#2a2a3e');
  const boardCss = argbToCss(boardBackgroundColor, '#1e2d3d');
  const labelBgCss = argbToCss(buttonsBackgroundColor, '#252535');
  const labelTextCss = argbToCss(buttonsIconsColor, '#fff');

  return (
    <Tile
      draggable={isReorderMode}
      isDragging={isDragging}
      isDragOver={isDragOver}
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      onDragEnd={onDragEnd}
    >
      {previewUrl ? (
        <PreviewCover src={previewUrl} alt={name} />
      ) : (
        <>
          <Background bg={bgCss} bgImage={gameplayBackgroundImageUrl} />
          <BoardArea>
            <BoardRect bg={boardCss} bgImage={boardBackgroundImageUrl} />
          </BoardArea>
        </>
      )}
      <LabelBar bg={labelBgCss}>
        <LabelText color={labelTextCss}>{name}</LabelText>
      </LabelBar>

      {isReorderMode ? (
        <>
          <OrderBadge>#{(positionIndex ?? 0) + 1}</OrderBadge>
          <DragHandle title="Drag to reorder">⠿</DragHandle>
          <ActionBar>
            <ActionBtn
              variant="move"
              disabled={!canMoveUp}
              onClick={(e) => {
                e.stopPropagation();
                onMoveUp?.();
              }}
            >
              ↑ Up
            </ActionBtn>
            <ActionBtn
              variant="move"
              disabled={!canMoveDown}
              onClick={(e) => {
                e.stopPropagation();
                onMoveDown?.();
              }}
            >
              ↓ Down
            </ActionBtn>
          </ActionBar>
        </>
      ) : (
        <ActionBar>
          <ActionBtn
            variant="edit"
            onClick={(e) => {
              e.stopPropagation();
              onEdit();
            }}
          >
            Edit
          </ActionBtn>
          <ActionBtn
            variant="delete"
            onClick={(e) => {
              e.stopPropagation();
              onDelete();
            }}
          >
            Delete
          </ActionBtn>
        </ActionBar>
      )}
    </Tile>
  );
};

export default ThemeGridItemPreview;
